import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { BOARD_LABELS } from '../data/curriculumData';
import TaskCreator from './TaskCreator';
import TaskDetail from './TaskDetail';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(val) {
  if (!val) return null;
  try {
    const d = typeof val.toDate === 'function' ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return null; }
}

// eslint-disable-next-line no-unused-vars
function isDueOverdue(dueDate) {
  if (!dueDate) return false;
  try { return new Date(dueDate) < new Date(); } catch { return false; }
}

// ── TeacherCurriculumView ─────────────────────────────────────────────────────

/**
 * Props:
 *   curriculum        {object}  — teacherCurricula document
 *   approvedStudents  [{studentId, studentName, studentEmail}]
 *   onBack()
 */
/**
 * Props:
 *   curriculum        {object}    – teacherCurricula document
 *   approvedStudents  {array}     – [{studentId, studentName, studentEmail}]
 *   allSubmissions    {array}     – pre-loaded submissions from TeacherDashboard (avoids duplicate listener)
 *   onBack()
 */
export default function TeacherCurriculumView({ curriculum, approvedStudents, allSubmissions = [], onBack }) {
  // ── Sub-view state
  // null                              → main curriculum view
  // { mode:'create', subjectName, chapterName } → TaskCreator (new)
  // { mode:'edit',   task }           → TaskCreator (edit)
  // { mode:'detail', task }           → TaskDetail
  const [subView, setSubView] = useState(null);

  // ── Tasks for this curriculum only (real-time — single non-duplicate query)
  const [tasks, setTasks] = useState([]);
  // ── Expanded state for subjects
  const [expandedSubjects, setExpandedSubjects] = useState({});

  // submissions come from parent (TeacherDashboard) to avoid duplicate listeners
  const submissions = allSubmissions;

  useEffect(() => {
    if (!curriculum?.id) return;
    const unsub = onSnapshot(
      query(collection(db, 'tasks'), where('curriculumId', '==', curriculum.id)),
      (snap) => setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [curriculum?.id]);

  // Build a lookup: chapterName → [task, ...]
  const tasksByChapter = {};
  tasks.forEach((t) => {
    const key = t.chapterName;
    if (!tasksByChapter[key]) tasksByChapter[key] = [];
    tasksByChapter[key].push(t);
  });

  const toggleSubject = (subjId) =>
    setExpandedSubjects((prev) => ({ ...prev, [subjId]: !prev[subjId] }));

  // ── Sub-view: Task Creator (new or edit)
  if (subView?.mode === 'create' || subView?.mode === 'edit') {
    return (
      <div className="dashboard-wrapper">
        {/* Thin header stays visible */}
        <header className="dashboard-header">
          <div className="header-left">
            <span className="header-logo">🪺 LittleNest</span>
            <div className="header-info">
              <h2>{curriculum.name}</h2>
              <p>Class {curriculum.grade} · Managing Tasks</p>
            </div>
          </div>
        </header>
        <TaskCreator
          curriculumId={curriculum.id}
          subjectName={subView.subjectName || subView.task?.subjectName}
          chapterName={subView.chapterName || subView.task?.chapterName}
          approvedStudents={approvedStudents}
          existingTask={subView.mode === 'edit' ? subView.task : null}
          onSave={() => setSubView(null)}
          onCancel={() => setSubView(null)}
        />
      </div>
    );
  }

  // ── Sub-view: Task Detail
  if (subView?.mode === 'detail') {
    return (
      <div className="dashboard-wrapper">
        <header className="dashboard-header">
          <div className="header-left">
            <span className="header-logo">🪺 LittleNest</span>
            <div className="header-info">
              <h2>{curriculum.name}</h2>
              <p>Class {curriculum.grade} · Task Detail</p>
            </div>
          </div>
        </header>
        <TaskDetail
          task={subView.task}
          approvedStudents={approvedStudents}
          onBack={() => setSubView(null)}
          onEdit={(task) => setSubView({ mode: 'edit', task })}
        />
      </div>
    );
  }

  // ── Main view: subjects + chapters + tasks
  const totalTasks = tasks.length;

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="header-left">
          <span className="header-logo">🪺 LittleNest</span>
          <div className="header-info">
            <h2 className="header-title">{curriculum.name}</h2>
            <p className="header-subtitle">
              Class {curriculum.grade}
              {curriculum.sourceBoard && ` · ${BOARD_LABELS[curriculum.sourceBoard] || curriculum.sourceBoard}`}
              {` · ${totalTasks} task${totalTasks !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <button className="btn-outline btn-outline--sm" onClick={onBack}>← Back to Dashboard</button>
      </header>

      <div className="section animate-in">
        {(curriculum.subjects || []).map((subj, si) => {
          const isOpen = expandedSubjects[subj.id];

          return (
            <div 
              key={subj.id} 
              className="cv-subject-block"
              style={{ 
                animationDelay: `${si * 0.05}s`,
                background: 'rgba(255, 255, 255, 0.4)',
                borderRadius: '16px',
                marginBottom: '16px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                overflow: 'hidden'
              }}
            >
              <button
                className="cv-subject-header"
                onClick={() => toggleSubject(subj.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                  background: isOpen ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'var(--primary)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  marginRight: '16px'
                }}>
                  {si + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{subj.name}</h3>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{subj.chapters?.length || 0} chapters</span>
                </div>
                <span style={{ transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                  <span className="cv-chevron">▼</span>
                </span>
              </button>

              {isOpen && (
                <div className="cv-chapter-list" style={{ padding: '0 20px 20px' }}>
                  {(subj.chapters || []).map((ch, ci) => {
                    const chTasks = tasksByChapter[ch.title] || [];
                    return (
                      <div key={ch.id} style={{ marginTop: '20px' }}>
                        <div className="cv-chapter-row" style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--primary)', opacity: 0.6 }}>{si+1}.{ci+1}</span>
                            <span style={{ fontWeight: '600', fontSize: '15px' }}>{ch.title}</span>
                          </div>
                          <button
                            className="btn-primary"
                            style={{ width: 'auto', padding: '6px 14px', fontSize: '13px' }}
                            onClick={() =>
                              setSubView({
                                mode: 'create',
                                subjectName: subj.name,
                                chapterName: ch.title,
                              })
                            }
                          >
                            + Add Task
                          </button>
                        </div>

                        {chTasks.length > 0 ? (
                          <div className="cv-task-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {chTasks.map((task, ti) => {
                              const submittedCount = submissions.filter(
                                (s) => s.taskId === task.id
                              ).length;
                              const assigned = task.assignedStudentIds?.length || 0;

                              return (
                                <div
                                  key={task.id}
                                  className="student-task-card animate-in"
                                  style={{ animationDelay: `${ti * 0.05}s`, padding: '12px 16px', cursor: 'pointer' }}
                                  onClick={() => setSubView({ mode: 'detail', task })}
                                >
                                  <div className="stc-left">
                                    <div className="student-avatar" style={{ width: 36, height: 36, fontSize: '14px' }}>📋</div>
                                    <div className="stc-info">
                                      <strong className="stc-name" style={{ fontSize: '14px' }}>{task.title}</strong>
                                      <span className="stc-email" style={{ fontSize: '12px' }}>
                                        {assigned} students · {formatDate(task.dueDate) ? `Due ${formatDate(task.dueDate)}` : 'No due date'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="stc-right">
                                    <span className={`chip chip--sm ${submittedCount === assigned && assigned > 0 ? 'chip--success' : ''}`} style={{ fontSize: '12px' }}>
                                      {submittedCount}/{assigned} Done
                                    </span>
                                    <span style={{ color: 'var(--text-secondary)', marginLeft: '8px' }}>›</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ padding: '12px', textAlign: 'center', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                            No tasks assigned for this chapter.
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {subj.chapters?.length === 0 && (
                    <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>No chapters in this subject.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
